import React, { useState, useEffect } from "react";
import { Box, Grid, Paper, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import SatelliteDataReportSubHeader from "../../../components/SatelliteDataReport/SatelliteDataReportSubHeader";
import LoaderContainer from "../../../components/LoaderContainer";
import ReportIrrigationPlanner from "../../../components/SatelliteDataReport/ReportIrrigationPlanner";
import SoilMoistureGraph from "../../../components/SatelliteCharts/SoilMoistureGraph";
import CropGrowthChart from "../../../components/SatelliteCharts/CropGrowthChart";
import axios from "axios";
import { SERVER_BASE_ADDRESS } from "../../../globals/const";
import { band10Colors } from "../../../components/SatelliteCharts/const";

const SatelliteDataSoilMoisturePage = ({ accessKey }) => {
  const { t } = useTranslation();
  const { encryptedId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [satelliteData, setSatelliteData] = useState(null);
  const [irrigationData, setIrrigationData] = useState([]);
  const [precipitationData, setPrecipitationData] = useState([]);
  const [farmId, setFarmId] = useState(null);
  const [date, setDate] = useState(null);

  useEffect(() => {
    if (!encryptedId) return;
    const dateString = encryptedId.substring(0, 8);
    const date = `${dateString.substring(0, 4)}-${dateString.substring(
      4,
      6
    )}-${dateString.substring(6, 8)}`;
    const farmId = encryptedId.substring(8);

    setFarmId(farmId);
    setDate(date);

    fetchData(farmId, date);
  }, [encryptedId]);

  const fetchData = async (farmId, date) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${SERVER_BASE_ADDRESS}/satellitedata/get-farm-data/${farmId}/?date=${date}`,
        {
          headers: {
            Authorization: `Token ${accessKey}`,
          },
        }
      );
      setSatelliteData(response.data.data);

      const fieldData = response.data.data.field_data;
      if (fieldData && typeof fieldData === "object") {
        const irrigation = [
          fieldData?.applied_water_fcdp0,
          fieldData?.applied_water_fcdp1,
          fieldData?.applied_water_fcdp2,
          fieldData?.applied_water_fcdp3,
          fieldData?.applied_water_fcdp4,
          fieldData?.applied_water_fcdp5,
          fieldData?.applied_water_fcdp6,
          fieldData?.applied_water_fcdp7,
        ];

        const precipitation = [
          fieldData?.precipitation_fcdp0,
          fieldData?.precipitation_fcdp1,
          fieldData?.precipitation_fcdp2,
          fieldData?.precipitation_fcdp3,
          fieldData?.precipitation_fcdp4,
          fieldData?.precipitation_fcdp5,
          fieldData?.precipitation_fcdp6,
          fieldData?.precipitation_fcdp7,
        ];

        setIrrigationData(irrigation);
        setPrecipitationData(precipitation);
      }

      setError(null);
    } catch (err) {
      console.error("Error fetching satellite data:", err);
      setError("Failed to fetch satellite data");
    } finally {
      setLoading(false);
    }
  };

  const legendColors = [
    "#5E4FA2",
    "#67C2A5",
    "#E6F599",
    "#FEDF8A",
    "#F36C43",
    "#9E0142",
  ];

  const legendUnits = {
    2: "",
    3: "m³/m³ or VMC(%)",
    4: "%",
    6: "mm/day",
    7: "kg/ha",
    8: "kg/ha/d",
    9: "cm or hPa (log10 scale)",
    12: "℃",
  };

  const legendSubtitles = [
    t("High"),
    t("Moderately High"),
    t("Moderate"),
    t("Moderately Low"),
    t("Low"),
    t("Very Low"),
  ];

  const renderLegend = (band) => {
    const selectedFile = satelliteData.html_files.find(
      (file) => file.band === band
    );

    if (!selectedFile) return null;

    const { legend_data } = selectedFile;
    const unit = legendUnits[band] || "";

    const processLegendValue = (value) => {
      if (typeof value !== "number") {
        return value;
      }
      if (band === 9) {
        return Math.log10(-1 * value).toFixed(4);
      }
      if (band === 2) {
        return (value / 100).toFixed(4);
      }
      return value.toFixed(4);
    };

    const legendDataToRender =
      band === 12 ? legend_data : legend_data.slice().reverse();

    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        sx={{ backgroundColor: "#fff" }}
        paddingBottom={0}
      >
        {legendDataToRender.map((value, index) => (
          <Box
            key={index}
            display="flex"
            flexDirection="row"
            alignItems="center"
            sx={{
              boxSizing: "border-box",
              border: "1px solid #D9D9D9",
              width: "100%",
              padding: "8px",
            }}
          >
            <Box
              width="20px"
              height="20px"
              bgcolor={band !== 10 ? legendColors[index] : band10Colors[index]}
              mr={2}
            />
            <Typography variant="body2" color={"#237D41"}>
              {legendSubtitles[index]}
            </Typography>
            <Typography
              variant="body2"
              color={"#237D41"}
              sx={{ marginLeft: "5px" }}
            >
              ({processLegendValue(value)})
            </Typography>
          </Box>
        ))}
        <Box
          sx={{
            backgroundColor: "#D3E5D9",
            width: "100%",
            padding: "10px",
            boxSizing: "border-box",
          }}
        >
          <Typography variant="body2" color={"#237D41"}>
            {unit}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderMap = (band, title) => {
    const htmlContent = satelliteData?.html_files.find(
      (file) => file.band === band
    )?.content;

    if (!htmlContent) return null;

    return (
      <Box sx={{}}>
        <iframe
          srcDoc={htmlContent}
          style={{
            width: "100%",
            height: "266px",
            border: "none",
          }}
          title={title}
        />
      </Box>
    );
  };

  return (
    <Box
      sx={{ maxWidth: 800, margin: "auto", backgroundColor: "#ffffff", mt: 1 }}
    >
      <LoaderContainer loading={loading}>
        {error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          satelliteData && (
            <>
              <Paper
                sx={{
                  mb: 1,
                  borderRadius: 0,
                  boxShadow: 0,
                  backgroundColor: "#237D410D",
                }}
              >
                <SatelliteDataReportSubHeader
                  text={t("Relative Soil Moisture in farm")}
                />
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 0,
                  }}
                >
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      {renderMap(10, t("Soil Moisture Status"))}
                    </Grid>
                    <Grid item xs={6}>
                      {renderLegend(10)}
                    </Grid>
                  </Grid>
                </Box>
                {/* Irrigation Planner  */}
                <SatelliteDataReportSubHeader
                  text={t("7 Day Irrigation Planner")}
                />
                <Box sx={{ px: 4, py: 2 }}>
                  <Box
                    sx={{
                      bgcolor: "#ffffff",
                      boxShadow: "#C3BCBC40",
                      height: "300px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ReportIrrigationPlanner
                      irrigationData={irrigationData}
                      precipitationData={precipitationData}
                      dateData={date}
                    />
                  </Box>
                </Box>
                {/* Soil moisture stats  */}
                <SatelliteDataReportSubHeader
                  text={t("Date wise soil moisture stats")}
                />
                <Box sx={{ px: 4, py: 2 }}>
                  <Box
                    sx={{
                      bgcolor: "#ffffff",
                      boxShadow: "#C3BCBC40",
                      padding: 2,
                    }}
                  >
                    {/* <Typography
                      sx={{ mb: 2, fontSize: "12px", fontWeight: "bold" }}
                    >
                      {t("Date wise soil moisture stats")}
                    </Typography> */}
                    <SoilMoistureGraph
                      date={date}
                      farmId={farmId}
                      height={300}
                    />
                  </Box>
                </Box>
                <SatelliteDataReportSubHeader text={t("Total Crop Growth")} />
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 0,
                  }}
                >
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      {renderMap(7, t("Total Crop Growth"))}
                    </Grid>
                    <Grid item xs={6}>
                      {renderLegend(7)}
                    </Grid>
                  </Grid>
                </Box>
                {/* Crop growth stats  */}
                <SatelliteDataReportSubHeader
                  text={t("Attainable vs Actual Crop Growth")}
                />
                <Box sx={{ px: 4, py: 2 }}>
                  <Box
                    sx={{
                      bgcolor: "#ffffff",
                      boxShadow: "#C3BCBC40",
                      padding: 2,
                    }}
                  >
                    <CropGrowthChart date={date} farmId={farmId} />
                  </Box>
                </Box>

                {/* Leaf Nitrogen */}
                <SatelliteDataReportSubHeader text={t("Leaf Nitrogen")} />
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 0,
                  }}
                >
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      {renderMap(4, t("Leaf Nitrogen"))}
                    </Grid>
                    <Grid item xs={6}>
                      {renderLegend(4)}
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </>
          )
        )}
      </LoaderContainer>
    </Box>
  );
};

export default SatelliteDataSoilMoisturePage;
